import { FC, useEffect, useState } from 'react';
import './ArticlesComments.scss';
import { useArticleComment } from '../../../providers/Articles/ArticlesCommentsProvider';
import moment from 'moment';
import { BvIcon } from 'bevi-icon';
import ArticlesCommentsInput from '../ArticlesCommentsInput/ArticlesCommentsInput';
import { useAuth } from '../../../providers/AuthProvider';
import DropdownMenuTable from '../../DropdownMenuTable/DropdownMenuTable';
import { Dropdown } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { UserRoleEnum } from '../../../enums/User/UserRoleEnum';

interface ArticlesCommentsProps {
  articleId?: number;
}

const apiUrl = process.env.REACT_APP_BEVIAJUDA_API ?? "";

const getInitials = (name?: string) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toLocaleUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toLocaleUpperCase();
}

const getUserPhotoUrl = (imagePath?: string) => {
  if (!imagePath) return null;
  const base = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
  const path = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
  return `${base}${path}`;
}

const ArticlesComments: FC<ArticlesCommentsProps> = ({ articleId }) => {
  const [selectedComment, setSelectedComment] = useState<any>(null);

  const { articleComments, error, setParams, handleDelete, handleList } = useArticleComment();
  const { userLogged } = useAuth();

  const handleDeleteComment = (item: any) => {
    Swal.fire({
      title: '<strong>Atenção?</strong>',
      html: `<span>Deseja realmente excluir o comentário?</span>`,
      icon: 'question',
      confirmButtonText: 'Sim, deletar!',
      cancelButtonText: 'Cancelar',
      showCancelButton: true,
      confirmButtonColor: 'var(--primary)',
    }).then(async (x: any) => {
      if (x.isConfirmed) {
        const ret = await handleDelete(item.id || 0).then();
        if (ret) {
          toast.success('Comentário removido com sucesso!');
          handleList();
        } else {
          toast.error(`Não foi possível remover o comentário. ${error}`);
        }
      }
    });
  }

  useEffect(() => {
    articleId && setParams({ filter: { article_id: articleId }, withPagination: false });
  }, [articleId]);

  return (
    <div className="ArticlesComments mt-4" data-testid="ArticlesComments">
      <h5>Comentários</h5>
      <p className="text-sm">Veja o que outros usuários estão dizendo sobre este artigo:</p>

      {articleComments && articleComments.length > 0 && (
        <div className="comments-list mt-4">
          {articleComments.map((comment, index) => {
            const photoUrl = getUserPhotoUrl(comment.user?.user_profile_image);
            return (
              <div key={comment?.id || index} className="comment mb-3 p-3 border rounded d-flex justify-content-between align-items-start gap-2">
                <div className="d-flex align-items-start gap-2">
                  {photoUrl ? (
                    <img src={photoUrl} alt="Avatar" className="comment-avatar" />
                  ) : (
                    <div className="comment-avatar comment-avatar-placeholder d-flex align-items-center justify-content-center">
                      <span className="comment-initials">{getInitials(comment.user?.name)}</span>
                    </div>
                  )}
                  <div>
                    <p className="m-0 text-sm" dangerouslySetInnerHTML={{ __html: comment?.comment.replace(/\n/g, "<br />") ?? '' }} />
                    <small className="text-muted text-xs">Comentado por {comment.user.name} {moment(comment.created_at).locale("pt-br").fromNow()}</small>
                  </div>
                </div>
                {userLogged?.id === comment.user.id && (
                  <div className="d-flex gap-2">
                    <DropdownMenuTable key={comment?.id || index} orientationIcon="vertical">
                      <Dropdown.Item className="border-radius-md py-2 d-flex align-items-center" onClick={() => setSelectedComment(comment)}>
                        <BvIcon name="editSquare" width={24} height={24} variant="duo" className="me-2" /> Editar
                      </Dropdown.Item>
                      <Dropdown.Item className="border-radius-md py-2 d-flex align-items-center" onClick={() => handleDeleteComment(comment)}>
                        <BvIcon name="trashDelete" width={24} height={24} variant="duo" className="me-2" /> Remover
                      </Dropdown.Item>
                    </DropdownMenuTable>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {userLogged?.role === UserRoleEnum.ADMIN && (
        <ArticlesCommentsInput articleId={articleId} comment={selectedComment} />
      )}
    </div>
  );
}

export default ArticlesComments;
